// @ts-strict-ignore
import { Icon, IconName } from '@usealloy/component-library';
import type { MultiSelectProps } from '@usealloy/component-library';
import React from 'react';

import { ActionType } from 'flow/store/engine/constants';
import { CUSTOM_LIST_SERVICE_KEY_IN_SERVICES, LIST_SERVICE_KEY_IN_SERVICES } from 'flow/service/services/constants';
import type { CreateServiceActionPayload } from 'flow/store/engine/create/actions/types';
import type { WorkflowState } from 'flow/store/redux/state';

import { formatCreatableElementsAsOptions } from '../BaseAddActionModal/helpers';
import type { Option } from './AddServiceOption';


/**
 * Used to filter the options shown in the Add Service Modal since we want to
 * search on more than just the name of the Service itself, specifically for
 * BUILD-2664 we want to search the `internalName` as well
 *
 * @param {Object} serviceOption - The current option we are iterating over
 * @param {String} inputValue - What the user has typed into the search field
 * @returns {Boolean}
 */
export function filterOptions(
  // NOTE: There is a type mismatch between the component library's react-select
  // (currently ver. 5.5.3) `Props` definition and Daffy's (currently ver. 3.2.0)
  // so we have to cast `serviceOption.data` below since we are using
  // `MultiSelectProps['filterOption']` type and not the newer react-select's
  // `Props<Option, true>['filterOption']` type
  serviceOption: Parameters<MultiSelectProps['filterOption']>[0],
  inputValue: string,
): boolean {
  if (!inputValue) {
    return true;
  }

  const normalizedInputValue = inputValue.toLocaleLowerCase();

  // NOTE: When we bump react-select in Daffy we can remove this cast
  const matchesInternalName = (
    (serviceOption.data as Option).meta.internalName
      .toLocaleLowerCase().includes(normalizedInputValue)
  );

  const matchesLabel = Boolean(serviceOption.label.toLowerCase().includes(normalizedInputValue));

  return matchesInternalName || matchesLabel;
}

/**
 * Converts services to dropdown options.
 * Services that are already part of the workflow are rendered as disabled since
 * they should still be present but not interactive
 */
export function formatServicesAsOptions(
  services: WorkflowState['workflowProps']['services'],
  servicesInWorkflow: WorkflowState['workflowData']['actions'][string][],
  shouldShowCheckbox?: boolean,
): Option[] {
  return formatCreatableElementsAsOptions({
    creatableElements: Object.values(services).filter(
      service => service.enabled || service.internal_name === LIST_SERVICE_KEY_IN_SERVICES,
    ),
    elementActionsInWorkflow: servicesInWorkflow,
    optionMapper: service => {
      const {
        name,
        internal_name: internalName,
        description,
      } = service;

      return {
        label: name,
        meta: {
          name,
          description,
          internalName,
          toolTipProps: {
            content: description,
          },
        },
        value: name,
        icon: <Icon icon={IconName.Service} />,
        shouldShowCheckbox,
      };
    },
    shouldNotDisableOptions: true,
  });
}

/**
 * If List services exists, this function will
 * add actual lists as type ServiceObject to services
 * and removes the generic services
 * If List services don't exist, return services
 */
export function expandListInServicesWithLists(
  services: WorkflowState['workflowProps']['services'],
  lists: WorkflowState['workflowProps']['lists'],
  customLists: WorkflowState['workflowProps']['customLists'],
): WorkflowState['workflowProps']['services'] {
  const {
    [LIST_SERVICE_KEY_IN_SERVICES]: genericListService,
    [CUSTOM_LIST_SERVICE_KEY_IN_SERVICES]: customListService,
    ...otherServices
  } = services;

  let servicesWithExpandedLists = {
    ...otherServices,
  };

  if (genericListService) {
    const listServicesByKey = lists
      .filter(list => !list.archived)
      .reduce((acc, {
        description, list_name: name,
      }) => {
        acc[name] = {
          ...genericListService,
          name,
          description: description || genericListService.description,
        };
        return acc;
      }, {});

    servicesWithExpandedLists = {
      ...servicesWithExpandedLists, ...listServicesByKey,
    };
  }

  if (customListService) {
    const customListServicesByKey = customLists
      .reduce((acc, {
        description, name,
      }) => {
        acc[name] = {
          ...customListService,
          name,
          description: description || customListService.description,
        };
        return acc;
      }, {});

    servicesWithExpandedLists = {
      ...servicesWithExpandedLists, ...customListServicesByKey,
    };
  }

  return servicesWithExpandedLists;
}

export function formatOptionsAsCreateActionPayload(
  serviceOptions: Option[],
  lists: WorkflowState['workflowProps']['lists'],
  customLists: WorkflowState['workflowProps']['customLists'],
): CreateServiceActionPayload[] {
  return serviceOptions
    .map(({ meta: { internalName, name } }) => {
      let listType = null;
      let listId = null; // custom_list_id for list or custom_list_token for custom list
      let listMajorVersion = null; // custom list's active major version

      switch (internalName) {
        case LIST_SERVICE_KEY_IN_SERVICES: {
          const listItem = lists.find(l => l.list_name === name);
          listId = listItem?.customer_list_id || null;
          if (listItem?.is_whitelist) {
            listType = 'whitelist';
          } else if (listItem?.is_blacklist) {
            listType = 'blacklist';
          }
          break;
        }

        case CUSTOM_LIST_SERVICE_KEY_IN_SERVICES: {
          const customListItem = customLists.find(cl => cl.name === name);
          listId = customListItem?.custom_list_token || null;
          listMajorVersion = customListItem?.active_major_version ?? null;
          break;
        }
      }

      return {
        type: ActionType.SERVICE,
        args: {
          internalName,
          name,
          listId,
          listMajorVersion,
          listType,
        },
      };
    });
}
